/**
 * CorpusLibrary — the /corpus library index, "El Índice".
 *
 * A typeset table of contents in two columns: every book is an index entry
 * (title · dotted leader · count) with its chapter chips in an
 * aligned numeric grid directly beneath it — one click to any chapter, no
 * disclosure step. The "Isaías 53" + Enter jump field is the fast path for
 * known targets.
 *
 * Drop-in: <CorpusLibrary books={books} onChapter={goChapter} /> replacing the
 * hero + BookNav block in CorpusReader's library-index branch. Styles live in
 * corpus-library.css (imported here). Type: Hanken Grotesk (UI) + the app's
 * Iowan/Palatino serif stack (display) — no third face, no font requests.
 */
import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { BowSigil } from "../components/Wordmark";
import type { Language } from "../strings";
import type {
  CorpusIndexBook,
  CorpusIndexChapter,
  CorpusIndexShort,
  LatestBook,
} from "./corpus-types";
import { FlechasShelf } from "./FlechasShelf";
import { latestFlechas } from "./flechas";
import "./corpus-library.css";

export interface CorpusLibraryProps {
  books: CorpusIndexBook[];
  shorts?: CorpusIndexShort[];
  onChapter: (slug: string, chapter: number) => void;
  onShort?: (videoId: string) => void;
  onShortIndex?: () => void;
  language: Language;
  /** Newest published book, from corpus-data/latest.json. Null hides the notice. */
  latest?: LatestBook | null;
  /** Optional content rendered between the hero and the index (e.g. the
   * "Continúa leyendo" plaque). */
  afterHero?: ReactNode;
}

/** Canon books carry order 0–65 (Bible order); the apocryphal appendix starts
 * at 66 (tobias, eclesiastico, baruc, nikodemos, jubileos, t12patriarcas, …). */
export const isAppendix = (book: CorpusIndexBook): boolean => book.order >= 66;

const CANON_HEADING = "El canon";
const APPENDIX_HEADING = "Libros apócrifos o gnósticos";

/** Accent-insensitive, case-insensitive matching ("isaias" → "Isaías"). */
function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

interface ParsedQuery {
  /** Book-name fragment with any trailing chapter number stripped. */
  text: string;
  chapter: number | null;
}

/** "isaias 53" → { text: "isaias", chapter: 53 }; "12 patriarcas" → text only. */
function parseQuery(raw: string): ParsedQuery {
  const trimmed = raw.trim();
  const tail = /(\d+)\s*$/.exec(trimmed);
  if (!tail || tail.index === 0) return { text: normalize(trimmed), chapter: null };
  const head = trimmed.slice(0, tail.index).trim();
  return { text: normalize(head), chapter: Number.parseInt(tail[1] ?? "", 10) };
}

function chapterTitle(book: CorpusIndexBook, ch: CorpusIndexChapter): string {
  return ch.label ? `${book.name} · ${ch.label}` : `${book.name} ${ch.chapter}`;
}

export function CorpusLibrary({
  books,
  shorts = [],
  onChapter,
  onShort,
  onShortIndex,
  language,
  afterHero,
  latest,
}: CorpusLibraryProps): JSX.Element {
  const [query, setQuery] = useState("");
  /** The chapter the reader just opened from here — keeps aria-current honest. */
  const [lastOpened, setLastOpened] = useState<{ slug: string; chapter: number } | null>(null);
  const bookEntries = useRef(new Map<string, HTMLElement>());

  const parsed = useMemo(() => parseQuery(query), [query]);

  const matches = useMemo(() => {
    if (parsed.text.length === 0) return books;
    const tokens = parsed.text.split(/\s+/).filter(Boolean);
    return books.filter((book) => {
      const name = normalize(book.name);
      return tokens.every((token) => name.includes(token));
    });
  }, [books, parsed.text]);

  const shortMatches = useMemo(() => {
    const normalizedQuery = normalize(query.trim());
    if (normalizedQuery.length === 0) return shorts;
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
    return shorts.filter((entry) => {
      const title = normalize(entry.title ?? "");
      return tokens.every((token) => title.includes(token));
    });
  }, [query, shorts]);

  /** "Isaías 53" fully resolved — Enter opens the chapter without any list. */
  const directHit = useMemo(() => {
    if (parsed.chapter === null) return null;
    const book = matches.find((candidate) =>
      candidate.chapters.some((ch) => ch.chapter === parsed.chapter),
    );
    return book ? { book, chapter: parsed.chapter } : null;
  }, [matches, parsed.chapter]);

  const canon = useMemo(() => matches.filter((book) => !isAppendix(book)), [matches]);
  const appendix = useMemo(() => matches.filter(isAppendix), [matches]);
  const totalChapters = useMemo(
    () => books.reduce((sum, book) => sum + book.chapters.length, 0),
    [books],
  );

  const openChapter = (slug: string, chapter: number): void => {
    setLastOpened({ slug, chapter });
    onChapter(slug, chapter);
  };

  const scrollBookIntoView = (slug: string): void => {
    requestAnimationFrame(() => {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      bookEntries.current
        .get(slug)
        ?.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" });
    });
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      if (directHit) {
        openChapter(directHit.book.slug, directHit.chapter);
        return;
      }
      const first = matches[0];
      if (first) {
        scrollBookIntoView(first.slug);
      } else if (shortMatches[0] && onShort) {
        onShort(shortMatches[0].video_id);
      }
    } else if (event.key === "Escape" && query.length > 0) {
      setQuery("");
    }
  };

  const hint = (() => {
    if (directHit) return `↳ ${directHit.book.name} ${directHit.chapter} — Intro para abrir`;
    if (parsed.text.length === 0) return "";
    const total = matches.length + shortMatches.length;
    if (total === 0) return "Ninguna enseñanza coincide — prueba «Isaías 53» o «gracias»";
    const parts = [
      matches.length > 0 ? `${matches.length} ${matches.length === 1 ? "libro" : "libros"}` : "",
      shortMatches.length > 0
        ? `${shortMatches.length} ${shortMatches.length === 1 ? "Flecha" : "Flechas"}`
        : "",
    ].filter(Boolean);
    return parts.join(" · ");
  })();

  const setBookEntry = (slug: string) => (el: HTMLElement | null) => {
    if (el) bookEntries.current.set(slug, el);
    else bookEntries.current.delete(slug);
  };

  const renderChip = (book: CorpusIndexBook, ch: CorpusIndexChapter): JSX.Element => {
    const active = lastOpened?.slug === book.slug && lastOpened.chapter === ch.chapter;
    return (
      <button
        key={ch.chapter}
        type="button"
        className={`creader-index__chip${ch.label ? " creader-index__chip--named" : ""}`}
        aria-current={active ? "page" : undefined}
        title={chapterTitle(book, ch)}
        onClick={() => openChapter(book.slug, ch.chapter)}
      >
        {ch.label ?? ch.chapter}
      </button>
    );
  };

  let revealIndex = -1;

  const renderBook = (book: CorpusIndexBook): JSX.Element => {
    revealIndex += 1;
    const appendixBook = isAppendix(book);
    const countText = book.chapters.length === 1 ? "1 capítulo" : `${book.chapters.length} capítulos`;
    return (
      <div
        key={book.slug}
        className="creader-index__book"
        style={{ "--reveal-i": revealIndex } as CSSProperties}
      >
        <div
          ref={setBookEntry(book.slug)}
          className={`creader-index__entry${appendixBook ? " creader-index__entry--appendix" : ""}`}
        >
          <span className="creader-index__title">{book.name}</span>
          <span className="creader-index__leader" aria-hidden="true" />
          <span className="creader-index__count">{countText}</span>
        </div>
        <div className="creader-index__chips" role="group" aria-label={`Capítulos de ${book.name}`}>
          {book.chapters.map((ch) => renderChip(book, ch))}
        </div>
      </div>
    );
  };

  return (
    <div className="creader-lib">
      <header className="creader-lib__hero">
        <span className="creader-lib__kicker">
          Biblioteca del estudio
          <span className="creader-lib__kicker-stats">
            {books.length} libros · {totalChapters} capítulos
            {shorts.length > 0
              ? ` · ${shorts.length} ${shorts.length === 1 ? "Flecha" : "Flechas"}`
              : ""}
          </span>
        </span>
        <h1 className="creader-lib__title">Las enseñanzas de Koresh, en texto</h1>
        {latest ? (
          <button
            type="button"
            className="creader-lib__new"
            onClick={() => onChapter(latest.slug, latest.first_chapter)}
            title={`Abrir ${latest.name}`}
          >
            <span className="creader-lib__new-tag">Nuevo</span>
            <span className="creader-lib__new-text">
              {latest.name} · {latest.chapters} {latest.chapters === 1 ? "capítulo" : "capítulos"}
            </span>
          </button>
        ) : null}
        <p className="creader-lib__sub">
          Lee las enseñanzas de Koresh, este es el texto que fundamenta cada respuesta.
        </p>
        <div className={`creader-lib__search${directHit ? " has-hit" : ""}`}>
          <BowSigil className="creader-lib__search-sigil" />
          <input
            className="creader-lib__search-input"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Ir a… «Isaías 53», «Rut», «Juan 3»"
            aria-label="Ir a un libro, capítulo o Flecha"
            autoComplete="off"
            spellCheck={false}
          />
          <span className="creader-lib__search-arrow" aria-hidden="true">
            <span className="creader-lib__search-line" />
            <span className="creader-lib__search-head" />
          </span>
        </div>
        <p className="creader-lib__hint" aria-live="polite">
          {hint}
        </p>
      </header>
      {afterHero}

      {onShort ? (
        <FlechasShelf
          entries={latestFlechas(shortMatches)}
          totalCount={shortMatches.length}
          language={language}
          onOpen={onShort}
          {...(onShortIndex ? { onViewAll: onShortIndex } : {})}
        />
      ) : null}

      <nav className="creader-index" aria-label="Libros del corpus">
        {canon.length > 0 ? <h2 className="creader-index__section">{CANON_HEADING}</h2> : null}
        {canon.map(renderBook)}
        {appendix.length > 0 ? <h2 className="creader-index__section">{APPENDIX_HEADING}</h2> : null}
        {appendix.map(renderBook)}
      </nav>

      {/* The corpus is actively indexed — a quiet note, not a banner. */}
      <aside className="creader-lib__notice">
        <BowSigil className="creader-lib__notice-sigil" />
        <div className="creader-lib__notice-body">
          <p className="creader-lib__notice-lead">La biblioteca sigue creciendo</p>
          <p className="creader-lib__notice-text">
            Añadimos las enseñanzas de Koresh continuamente. Si un libro o capítulo aún no aparece,
            vuelve a visitarnos en los próximos días.
          </p>
        </div>
      </aside>
    </div>
  );
}
